import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Users, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { Money } from "@/components/common/Money";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentPool, useLeaderboard, useMyGiftScore } from "@/hooks/useGift";
import { useReferralStats } from "@/hooks/useReferral";
import { GIFT_GRACE_DAYS, GIFT_REFERRALS_REQUIRED, GIFT_TOP_RANK } from "@/lib/constants";
import { addDays, differenceInDays, differenceInHours, endOfWeek, formatDistanceStrict, parseISO } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

export function Gift() {
  const { i18n } = useTranslation();
  const { profile } = useAuth();
  const pool = useCurrentPool();
  const myScore = useMyGiftScore();
  const lb = useLeaderboard(GIFT_TOP_RANK);
  const referralStats = useReferralStats();
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    const update = () => {
      const end = endOfWeek(new Date(), { weekStartsOn: 1 });
      setCountdown(formatDistanceStrict(end, new Date(), { locale: i18n.language === "en" ? enUS : fr }));
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [i18n.language]);

  // Le nombre de filleuls payants est calculé depuis la table referrals
  // (source de vérité). On garde is_gift_eligible comme fallback serveur.
  const paid = referralStats.paid;
  const eligible = (profile?.is_gift_eligible ?? false) || paid >= GIFT_REFERRALS_REQUIRED;
  const myRank = myScore.data?.rank ?? null;

  // Compte à rebours sur GIFT_GRACE_DAYS jours depuis l'inscription pour
  // motiver le user à inviter ses 3 amis avant la deadline.
  const deadline = profile?.created_at
    ? addDays(parseISO(profile.created_at), GIFT_GRACE_DAYS)
    : null;
  const now = new Date();
  const daysLeft = deadline ? differenceInDays(deadline, now) : null;
  const hoursLeft = deadline ? differenceInHours(deadline, now) : null;
  const expired = deadline ? deadline.getTime() <= now.getTime() : false;
  const elapsedDays = deadline
    ? Math.min(GIFT_GRACE_DAYS, Math.max(0, GIFT_GRACE_DAYS - differenceInDays(deadline, now)))
    : 0;
  const timeProgressPct = (elapsedDays / GIFT_GRACE_DAYS) * 100;

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <div className="flex-1 text-center">
          <h1 className="text-base font-semibold tracking-tight leading-none">Gift</h1>
          <p className="text-2xs text-muted-foreground mt-0.5">Cagnotte hebdomadaire</p>
        </div>
        <div className="size-8" />
      </header>

      {!eligible ? (
        <section className="rounded-2xl border border-white/[0.06] bg-card p-6 text-center">
          <h2 className="text-base font-semibold">Pas encore éligible</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Il te faut <strong className="text-foreground">{GIFT_REFERRALS_REQUIRED}</strong> parrainages payants
          </p>

          {/* Progression parrainages */}
          <div className="mt-4">
            <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${Math.min(100, (paid / GIFT_REFERRALS_REQUIRED) * 100)}%` }}
              />
            </div>
            <p className="mt-2 text-2xs text-muted-foreground tabular-nums">
              {paid} / {GIFT_REFERRALS_REQUIRED} parrainages
            </p>
          </div>

          {/* Compte à rebours sur GIFT_GRACE_DAYS jours depuis l'inscription */}
          {deadline && (
            <div className="mt-5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-left">
              <div className="flex items-center justify-between gap-2">
                <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">
                  Délai restant
                </p>
                <p className={cn(
                  "text-xs font-semibold tabular-nums",
                  expired ? "text-destructive"
                    : (daysLeft ?? 0) <= 3 ? "text-warning"
                      : "text-foreground"
                )}>
                  {expired
                    ? "Délai dépassé"
                    : (daysLeft ?? 0) > 0
                      ? `${daysLeft} ${(daysLeft ?? 0) > 1 ? "jours" : "jour"}`
                      : `${Math.max(0, hoursLeft ?? 0)} h`}
                </p>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all duration-300",
                    expired ? "bg-destructive"
                      : (daysLeft ?? 0) <= 3 ? "bg-warning"
                        : "bg-success"
                  )}
                  style={{ width: `${Math.min(100, Math.max(0, timeProgressPct))}%` }}
                />
              </div>
              <p className="mt-2 text-2xs text-muted-foreground">
                {expired
                  ? `Ton délai de ${GIFT_GRACE_DAYS} jours pour parrainer 3 amis est dépassé. Tu peux toujours continuer pour les futures cagnottes.`
                  : `${GIFT_GRACE_DAYS} jours à compter de ton inscription pour trouver tes ${GIFT_REFERRALS_REQUIRED} amis.`}
              </p>
            </div>
          )}

          <Button asChild className="mt-5">
            <Link to="/referral"><Users className="size-3.5" /> Inviter</Link>
          </Button>
        </section>
      ) : (
        <>
          <section className="rounded-2xl border border-white/[0.06] bg-card p-5 shadow-card">
            <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Cagnotte semaine</p>
            <p className="mt-1 text-[40px] leading-none font-bold num tracking-tight">
              <Money amount={pool.data?.total_amount ?? 0} />
            </p>
            <p className="mt-2 text-xs text-muted-foreground">Reset dans {countdown}</p>
          </section>

          <section className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-white/[0.06] bg-card px-4 py-3">
              <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Mon rang</p>
              <p className="mt-1 text-2xl font-bold num">{myRank ?? "—"}</p>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-card px-4 py-3">
              <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Mes points</p>
              <p className="mt-1 text-2xl font-bold num">{myScore.data?.points ?? 0}</p>
            </div>
          </section>
        </>
      )}

      <section className="space-y-2">
        <div className="flex items-center justify-between px-0.5">
          <h2 className="text-sm font-semibold tracking-tight">Classement</h2>
          <span className="text-2xs text-muted-foreground">TOP {GIFT_TOP_RANK}</span>
        </div>
        {lb.isLoading ? (
          <InlineLoading />
        ) : (lb.data?.length ?? 0) === 0 ? (
          <EmptyState icon={Trophy} title="Pas encore de score" />
        ) : (
          <div className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06]">
            {(lb.data ?? []).map((row, idx) => (
              <div
                key={row.id}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5",
                  row.user_id === profile?.id && "bg-primary/[0.05]"
                )}
              >
                <span className={cn(
                  "w-6 text-center text-xs font-semibold tabular-nums",
                  idx === 0 ? "text-warning" : idx <= 2 ? "text-foreground" : "text-muted-foreground"
                )}>
                  {idx + 1}
                </span>
                <InitialsAvatar name={row.users?.first_name} seed={row.user_id} size="sm" />
                <span className="flex-1 text-sm font-medium truncate">
                  {row.users?.first_name ?? "—"}
                  {row.user_id === profile?.id && <span className="text-primary ml-1 text-2xs">· toi</span>}
                </span>
                <span className="text-sm font-semibold text-muted-foreground tabular-nums">
                  {row.points} <span className="text-2xs text-muted-foreground/70">pts</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
